// Derive a representative IANA timezone for a car from the location data the
// car row already exposes (latitude / longitude / country_code).
//
// Task #3972 (F.12): the Event Rental modal shows pick-up / drop-off times in
// the BROWSER's local timezone, which is wrong for a renter in a different
// zone — the host means the car's local wall-clock. The car table has no
// timezone column, and adding one + a prod backfill is the separate
// PROD-APPLY follow-on. So we derive a zone client-side, purely for DISPLAY
// (labelling the times), never for the quote / availability math.
//
// Strategy: for the handful of countries that span several zones (US, CA, AU,
// RU, BR) we refine by longitude/latitude when coordinates are present;
// otherwise we fall back to a single representative IANA zone per country.

export interface CarLocationLike {
  latitude?: number | null;
  longitude?: number | null;
  country_code?: string | null;
}

// One representative IANA zone per ISO-3166 alpha-2 country. For multi-zone
// countries this is the fallback used when no coordinates are available.
const COUNTRY_TIMEZONE: Record<string, string> = {
  // Europe
  GB: "Europe/London",
  IE: "Europe/Dublin",
  PT: "Europe/Lisbon",
  ES: "Europe/Madrid",
  FR: "Europe/Paris",
  BE: "Europe/Brussels",
  NL: "Europe/Amsterdam",
  LU: "Europe/Luxembourg",
  DE: "Europe/Berlin",
  CH: "Europe/Zurich",
  AT: "Europe/Vienna",
  IT: "Europe/Rome",
  MT: "Europe/Malta",
  DK: "Europe/Copenhagen",
  NO: "Europe/Oslo",
  SE: "Europe/Stockholm",
  FI: "Europe/Helsinki",
  IS: "Atlantic/Reykjavik",
  PL: "Europe/Warsaw",
  CZ: "Europe/Prague",
  SK: "Europe/Bratislava",
  HU: "Europe/Budapest",
  SI: "Europe/Ljubljana",
  HR: "Europe/Zagreb",
  RS: "Europe/Belgrade",
  BA: "Europe/Sarajevo",
  ME: "Europe/Podgorica",
  MK: "Europe/Skopje",
  AL: "Europe/Tirane",
  GR: "Europe/Athens",
  BG: "Europe/Sofia",
  RO: "Europe/Bucharest",
  MD: "Europe/Chisinau",
  UA: "Europe/Kyiv",
  BY: "Europe/Minsk",
  LT: "Europe/Vilnius",
  LV: "Europe/Riga",
  EE: "Europe/Tallinn",
  CY: "Asia/Nicosia",
  TR: "Europe/Istanbul",
  // Middle East
  AE: "Asia/Dubai",
  SA: "Asia/Riyadh",
  QA: "Asia/Qatar",
  KW: "Asia/Kuwait",
  BH: "Asia/Bahrain",
  OM: "Asia/Muscat",
  IL: "Asia/Jerusalem",
  JO: "Asia/Amman",
  LB: "Asia/Beirut",
  // Asia
  IN: "Asia/Kolkata",
  PK: "Asia/Karachi",
  TH: "Asia/Bangkok",
  VN: "Asia/Ho_Chi_Minh",
  SG: "Asia/Singapore",
  MY: "Asia/Kuala_Lumpur",
  ID: "Asia/Jakarta",
  PH: "Asia/Manila",
  HK: "Asia/Hong_Kong",
  TW: "Asia/Taipei",
  JP: "Asia/Tokyo",
  KR: "Asia/Seoul",
  CN: "Asia/Shanghai",
  // Africa
  ZA: "Africa/Johannesburg",
  EG: "Africa/Cairo",
  MA: "Africa/Casablanca",
  KE: "Africa/Nairobi",
  NG: "Africa/Lagos",
  // Americas
  US: "America/New_York",
  CA: "America/Toronto",
  MX: "America/Mexico_City",
  BR: "America/Sao_Paulo",
  AR: "America/Argentina/Buenos_Aires",
  CL: "America/Santiago",
  CO: "America/Bogota",
  PE: "America/Lima",
  // Oceania
  AU: "Australia/Sydney",
  NZ: "Pacific/Auckland",
  // Multi-zone fallback
  RU: "Europe/Moscow",
};

function usZone(lat: number | null, lng: number | null): string {
  if (lng == null) return "America/New_York";
  // Non-contiguous zones first.
  if (lng <= -150 && (lat == null || lat < 30)) return "Pacific/Honolulu";
  if (lng <= -130 && (lat == null || lat > 50)) return "America/Anchorage";
  if (lng > -87.5) return "America/New_York";
  if (lng > -102) return "America/Chicago";
  if (lng > -115) return "America/Denver";
  return "America/Los_Angeles";
}

function caZone(lng: number): string {
  if (lng < -120) return "America/Vancouver";
  if (lng < -102) return "America/Edmonton";
  if (lng < -90) return "America/Winnipeg";
  if (lng < -60) return "America/Toronto";
  return "America/Halifax";
}

function auZone(lng: number): string {
  if (lng < 129) return "Australia/Perth";
  if (lng < 138) return "Australia/Adelaide";
  return "Australia/Sydney";
}

function brZone(lng: number): string {
  if (lng < -60) return "America/Manaus";
  return "America/Sao_Paulo";
}

function ruZone(lng: number): string {
  if (lng < 40) return "Europe/Moscow";
  if (lng < 60) return "Asia/Yekaterinburg";
  if (lng < 90) return "Asia/Novosibirsk";
  if (lng < 120) return "Asia/Irkutsk";
  return "Asia/Vladivostok";
}

/**
 * Best-effort IANA timezone for a car, derived from existing fields. Returns
 * `null` when there's no usable country/coordinate data, so callers can hide
 * the label rather than show a wrong/empty zone.
 */
export function deriveCarTimezone(car: CarLocationLike): string | null {
  const cc = (car.country_code || "").trim().toUpperCase();
  const lat = typeof car.latitude === "number" && Number.isFinite(car.latitude) ? car.latitude : null;
  const lng = typeof car.longitude === "number" && Number.isFinite(car.longitude) ? car.longitude : null;

  if (cc === "US") return usZone(lat, lng);
  if (cc === "CA" && lng != null) return caZone(lng);
  if (cc === "AU" && lng != null) return auZone(lng);
  if (cc === "BR" && lng != null) return brZone(lng);
  if (cc === "RU" && lng != null) return ruZone(lng);

  if (cc && COUNTRY_TIMEZONE[cc]) return COUNTRY_TIMEZONE[cc];
  return null;
}
